import {Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {HttpClient, HttpErrorResponse, HttpHeaders} from "@angular/common/http";
import {ApiUrl} from "../../../shared/models/apiurl.models";
import {animate, keyframes, state, style, transition, trigger} from "@angular/animations";
import {TermsConsentDialogComponent} from "../terms-consent-dialog/terms-consent-dialog.component";
import {AuthSessionService} from "../../../services/auth-session.service";
import {LoginAction, LoginService} from "../../session/login/login.service";


export interface ConfirmValidation{
  acceptAdv: boolean,
  acceptTerms: boolean,
  contactPerson: string,
  email: string,
  phoneNumber: string,
  userId: number,
  codeValidation?: number | string | null,
  validPhone?: boolean,
}

export interface PhoneValidationDialogData {
  contact: ConfirmValidation;
  /** The current terms are already approved on the server (box shown ticked + locked). */
  termsLocked?: boolean;
}

export interface PhoneValidationResult {
  verified: boolean;
  /** The number belongs to another account - nothing was verified. */
  linkedToOtherAccount?: boolean;
  message?: string;
  phoneNumber: string;
  acceptTerms: boolean;
  acceptAdv: boolean;
}

/** Mirrors the server rules (clinetApi.views OTP_*); the server is authoritative. */
const RESEND_COOLDOWN = 90;      // 1:30 between two codes
const MAX_ATTEMPTS = 8;          // wrong guesses per code
const MAX_SENDS = 4;             // codes per 3 hours

type Phase = 'sending' | 'ready' | 'verifying' | 'verified';

@Component({
    selector: 'app-session-dialog',
    templateUrl: './phone-validation-dialog.component.html',
    styleUrls: ['./phone-validation-dialog.component.scss'],
    animations: [
        trigger('shakeit', [
            state('shakestart', style({ transform: 'scale(1)' })),
            state('shakeend', style({ transform: 'scale(1)' })),
            transition('shakestart <=> shakeend', animate('600ms ease-in', keyframes([
                style({ transform: 'translate3d(-2px, 0, 0)', offset: 0.1 }),
                style({ transform: 'translate3d(4px, 0, 0)', offset: 0.25 }),
                style({ transform: 'translate3d(-6px, 0, 0)', offset: 0.4 }),
                style({ transform: 'translate3d(6px, 0, 0)', offset: 0.55 }),
                style({ transform: 'translate3d(-4px, 0, 0)', offset: 0.7 }),
                style({ transform: 'translate3d(2px, 0, 0)', offset: 0.85 }),
                style({ transform: 'translate3d(0, 0, 0)', offset: 1 }),
            ]))),
        ])
    ],
    standalone: false
})
export class PhoneValidationDialogComponent implements OnInit, OnDestroy {
  @ViewChild('codeInput') codeInput?: ElementRef<HTMLInputElement>;

  private options = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': this.apiUrl.getJWTToken()
    })
  }

  readonly contact: ConfirmValidation;
  /** Set when the server says the number is another account's number. */
  linkedMessage = '';
  readonly termsLocked: boolean;

  acceptTerms = false;
  acceptAdv = false;
  code = '';

  phase: Phase = 'sending';
  codeSent = false;
  info = '';
  sendError = '';
  codeError = '';
  shakeState = 'shakeend';

  maxAttempts = MAX_ATTEMPTS;
  attemptsLeft = MAX_ATTEMPTS;
  maxSends = MAX_SENDS;
  sendsLeft: number | null = null;

  /** Seconds until another code may be requested. */
  cooldown = 0;
  /** Seconds until the 3-hour cap frees a slot (0 = not capped). */
  limitWait = 0;
  private timer: any = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: PhoneValidationDialogData | ConfirmValidation,
    public apiUrl: ApiUrl,
    public http: HttpClient,
    private dialog: MatDialog,
    private authSession: AuthSessionService,
    private loginService: LoginService,
    public dialogRef: MatDialogRef<PhoneValidationDialogComponent, PhoneValidationResult>) {
    // Accept the old call shape (the contact itself) as well as {contact, termsLocked}.
    const wrapped = (data as PhoneValidationDialogData)?.contact !== undefined;
    this.contact = wrapped ? (data as PhoneValidationDialogData).contact : (data as ConfirmValidation);
    this.termsLocked = wrapped ? !!(data as PhoneValidationDialogData).termsLocked : false;
    this.acceptTerms = this.termsLocked || !!this.contact?.acceptTerms;
    this.acceptAdv = this.acceptTerms && !!this.contact?.acceptAdv;
  }

  ngOnInit(): void {
    this.sendCode();
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  // ── View helpers ──────────────────────────────────────────

  get maskedPhone(): string {
    const digits = String(this.contact?.phoneNumber ?? '').replace(/\D/g, '');
    if (digits.length < 7) return this.contact?.phoneNumber || '';
    const local = digits.startsWith('972') ? '0' + digits.slice(3) : digits;
    return local.slice(0, 3) + '-' + '•'.repeat(Math.max(0, local.length - 6)) + local.slice(-3);
  }

  get bothConsents(): boolean {
    return this.acceptTerms && this.acceptAdv;
  }

  get codeComplete(): boolean {
    return /^\d{6}$/.test(this.code);
  }

  get canVerify(): boolean {
    return this.phase === 'ready' && this.codeSent && this.codeComplete
      && this.bothConsents && this.attemptsLeft > 0;
  }

  get canResend(): boolean {
    return (this.phase === 'ready') && this.cooldown <= 0 && this.limitWait <= 0 && this.sendsLeft !== 0;
  }

  get locked(): boolean {
    return this.codeSent && this.attemptsLeft <= 0;
  }

  get verifyHint(): string {
    if (this.phase === 'verified') return '';
    if (!this.codeSent) return '';
    if (this.locked) return 'יש לשלוח קוד חדש כדי להמשיך';
    if (!this.codeComplete) return 'הזינו את 6 ספרות הקוד';
    if (!this.bothConsents) return 'יש לסמן את שתי ההסכמות כדי לאמת';
    return '';
  }

  formatTime(sec: number): string {
    const s = Math.max(0, Math.ceil(sec));
    if (s >= 3600) {
      const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
      return `${h}:${String(m).padStart(2, '0')} שעות`;
    }
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }

  // ── Consents ──────────────────────────────────────────────

  onTermsClick(event: MouseEvent): void {
    if (this.termsLocked || this.phase === 'verified') {
      event.preventDefault();
      return;
    }
    // Same rule as the contact form: the box ticks after reading the תקנון.
    if (!this.acceptTerms) {
      event.preventDefault();
      this.openTerms();
    }
  }

  onTermsChange(checked: boolean): void {
    this.acceptTerms = checked;
    if (!checked) this.acceptAdv = false;
  }

  openTerms(event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.dialog.open(TermsConsentDialogComponent, {
      width: '760px',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: 'calc(100vh - 24px)',
      direction: 'rtl',
      autoFocus: false
    }).afterClosed().subscribe((accepted: boolean | undefined) => {
      if (accepted) this.acceptTerms = true;
    });
  }

  onAdvChange(checked: boolean): void {
    this.acceptAdv = checked && this.acceptTerms;
  }

  // ── Code input ────────────────────────────────────────────

  onCodeInput(value: string): void {
    this.code = String(value ?? '').replace(/\D/g, '').slice(0, 6);
    if (this.codeInput && this.codeInput.nativeElement.value !== this.code) {
      this.codeInput.nativeElement.value = this.code;
    }
    if (this.codeError && this.code.length) this.codeError = '';
  }

  // ── Server calls ──────────────────────────────────────────

  private payload(code: string | null): any {
    return {
      ...this.contact,
      acceptTerms: this.acceptTerms,
      // The server refuses marketing consent without the terms.
      acceptAdv: this.acceptTerms && this.acceptAdv,
      codeValidation: code,
    };
  }

  sendCode(): void {
    if (this.phase === 'verifying' || this.phase === 'verified') return;
    if (this.codeSent && !this.canResend) return;
    this.phase = 'sending';
    this.sendError = '';
    this.info = '';
    this.http.post<any>(this.apiUrl.getPhoneValidationUrl(), this.payload(null), this.options).subscribe({
      next: res => {
        this.codeSent = true;
        this.code = '';
        this.codeError = '';
        this.maxAttempts = res?.maxAttempts || MAX_ATTEMPTS;
        this.attemptsLeft = this.maxAttempts;
        this.maxSends = res?.maxSends || MAX_SENDS;
        this.sendsLeft = typeof res?.sendsLeft === 'number' ? res.sendsLeft : null;
        this.info = 'קוד אימות נשלח ב-SMS';
        this.startCooldown(res?.cooldown || RESEND_COOLDOWN);
        this.phase = 'ready';
        this.focusCode();
      },
      error: (err: HttpErrorResponse) => {
        this.phase = 'ready';
        const body = err?.error || {};
        const retry = Number(body.retryAfter) || this.retryAfterHeader(err);
        if (this.handleLinked(err)) return;
        if (typeof body.sendsLeft === 'number') this.sendsLeft = body.sendsLeft;
        if (body.maxSends) this.maxSends = body.maxSends;
        if (err.status === 429 && body.statusCode === 'OTP_COOLDOWN') {
          // A code was sent moments ago and is still valid - let the user type it.
          this.codeSent = true;
          this.info = 'קוד נשלח לאחרונה למספר זה. ניתן להזין אותו או לבקש קוד חדש בסיום הספירה.';
          this.startCooldown(retry || RESEND_COOLDOWN);
          this.focusCode();
          return;
        }
        if (err.status === 429) {
          this.codeSent = this.codeSent || body.statusCode === 'OTP_SEND_LIMIT';
          this.sendsLeft = 0;
          this.sendError = body.message
            || `הגעת למגבלה של ${this.maxSends} קודים ב-3 שעות. נסו שוב מאוחר יותר.`;
          this.startLimit(retry || 0);
          return;
        }
        this.sendError = body.message || body.description || 'לא הצלחנו לשלוח קוד אימות. נסו שוב בעוד רגע.';
      }
    });
  }

  verify(): void {
    if (!this.canVerify) {
      if (this.codeSent && !this.bothConsents) this.shake();
      return;
    }
    this.phase = 'verifying';
    this.codeError = '';
    this.http.post<any>(this.apiUrl.getPhoneValidationUrl(), this.payload(this.code), this.options).subscribe({
      next: res => {
        // A wrong code answers 203 (a 2xx), so check the body.
        if (res?.status === 'success') {
          this.onVerified(res);
        } else {
          this.onWrongCode(res);
        }
      },
      error: (err: HttpErrorResponse) => {
        if (this.handleLinked(err)) return;
        if (err.status === 429) {
          this.phase = 'ready';
          this.codeError = 'יותר מדי ניסיונות. נסו שוב בעוד מספר דקות.';
          this.shake();
          return;
        }
        this.onWrongCode(err?.error);
      }
    });
  }

  private onVerified(res: any): void {
    this.phase = 'verified';
    this.stopTimer();
    this.contact.validPhone = true;
    this.contact.acceptTerms = this.acceptTerms;
    this.contact.acceptAdv = this.acceptTerms && this.acceptAdv;
    setTimeout(() => this.dialogRef.close({
      verified: true,
      phoneNumber: this.contact.phoneNumber,
      acceptTerms: this.contact.acceptTerms,
      acceptAdv: this.contact.acceptAdv,
    }), 1100);
  }

  private onWrongCode(body: any): void {
    this.phase = 'ready';
    const left = typeof body?.attemptsLeft === 'number'
      ? body.attemptsLeft
      : Math.max(0, this.attemptsLeft - 1);
    this.attemptsLeft = left;
    if (body?.maxAttempts) this.maxAttempts = body.maxAttempts;
    if (typeof body?.sendsLeft === 'number') this.sendsLeft = body.sendsLeft;
    if (body?.statusCode === 'CODE_EXPIRED') this.attemptsLeft = 0;
    this.codeError = body?.message
      || (left > 0 ? `הקוד שגוי. נותרו ${left} ניסיונות.` : 'ניצלת את כל הניסיונות לקוד זה. שלחו קוד חדש.');
    this.code = '';
    if (this.codeInput) this.codeInput.nativeElement.value = '';
    this.shake();
    if (left > 0) this.focusCode();
  }

  // ── Number linked to another account ─────────────────────

  private handleLinked(err: HttpErrorResponse): boolean {
    const body = err?.error || {};
    if (body.statusCode !== 'PHONE_LINKED_OTHER_ACCOUNT' && body.error !== 'PHONE_LINKED_OTHER_ACCOUNT') {
      return false;
    }
    this.phase = 'ready';
    this.stopTimer();
    this.cooldown = 0;
    this.codeSent = false;
    this.code = '';
    this.linkedMessage = body.message
      || 'מספר הטלפון הזה מקושר לחשבון אחר. יש להתחבר לחשבון הנכון כדי להשתמש בו.';
    return true;
  }

  /** Close, log out of this account and open the login form. */
  switchAccount(): void {
    this.dialogRef.close({
      verified: false, linkedToOtherAccount: true, message: this.linkedMessage,
      phoneNumber: this.contact.phoneNumber, acceptTerms: this.acceptTerms, acceptAdv: this.acceptAdv,
    });
    this.authSession.logout().subscribe({error: () => {}});
    ['token', 'is_logged_in', 'is_superuser', 'first_name', 'last_name', 'email', 'avatar', 'userDetails']
      .forEach(k => localStorage.removeItem(k));
    setTimeout(() => this.loginService.sendClickEvent(LoginAction.Open), 150);
  }

  /** Close and let the user type a different number. */
  changeNumber(): void {
    this.dialogRef.close({
      verified: false, linkedToOtherAccount: true, message: this.linkedMessage,
      phoneNumber: this.contact.phoneNumber, acceptTerms: this.acceptTerms, acceptAdv: this.acceptAdv,
    });
  }

  // ── Timers ────────────────────────────────────────────────

  private startCooldown(seconds: number): void {
    this.cooldown = Math.max(0, Math.round(seconds));
    this.ensureTimer();
  }

  private startLimit(seconds: number): void {
    this.limitWait = Math.max(0, Math.round(seconds));
    this.ensureTimer();
  }

  private ensureTimer(): void {
    if (this.timer) return;
    this.timer = setInterval(() => {
      if (this.cooldown > 0) this.cooldown--;
      if (this.limitWait > 0) {
        this.limitWait--;
        if (this.limitWait === 0) { this.sendsLeft = null; this.sendError = ''; }
      }
      if (this.cooldown <= 0 && this.limitWait <= 0) this.stopTimer();
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private retryAfterHeader(err: HttpErrorResponse): number {
    const fromHeader = Number(err?.headers?.get?.('Retry-After'));
    if (fromHeader > 0) return fromHeader;
    const m = /(\d+)\s*sec/.exec(String(err?.error?.detail || ''));
    return m ? Number(m[1]) : 0;
  }

  private focusCode(): void {
    setTimeout(() => this.codeInput?.nativeElement?.focus(), 50);
  }

  shake(): void {
    this.shakeState = this.shakeState === 'shakestart' ? 'shakeend' : 'shakestart';
  }

  close(): void {
    this.dialogRef.close();
  }
}
